/* global setTimeout, clearTimeout, setInterval, clearInterval */
/**
 * WorkdayContext.js
 *
 * Provider + hooks of the "Munkanap" (workday clock-in / clock-out) feature. Mounted once in `App.jsx`, inside
 * `AppProviders` (Redux, navigation, system) and above the navigator, so EVERY module sees the same state.
 *
 * Responsibilities
 * - state: `GET /workday/current` on login, on AppState 'active', every 60 s while foregrounded and on the realtime
 *   `workday:changed` event of the user's room. Everything is FAIL-SOFT: a 404/405 (backend not deployed) flips
 *   `supported` to false, which hides the whole feature; nothing throws, nothing blocks the UI.
 * - actions: `start`, `stop({stopTimers})`, `snooze(minutes)`, `setWorkMode(mode)`, each updating the state from the response.
 * - office / home office: when the store enabled work location (`gpsWorkMode`) the server decides it from the clock-in
 *   fix; otherwise the user's remembered `workModePref` is sent and can be changed while the day runs.
 * - end-of-day reminder: the server push is the single OS notification (a legacy local one is cancelled once); opens the in-app reminder modal when
 *   it is due (timer + foreground return) and when the notification / server push is tapped (`workdayBus`).
 * - sequencing: reports which single surface (briefing / reminder / start sheet) may be shown ({@link pickSurface}).
 *
 * The live seconds are NOT part of the context value (that would re-render every consumer each tick): use
 * {@link useWorkdayLive} in the few components that draw a ticking clock.
 *
 * @module helpers/WorkdayContext
 */

import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AppState, Platform} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {io as createSocketClient} from 'socket.io-client';
import i18n from '../../i18n';
import crossToast from './crossToast';
import {ApiConfig} from '../config/ApiConfig';
import {getAccessToken, getUserInfo} from './storage';
import {navigationRef} from './navigationRef';
import {
    fetchWorkdayState, isUnsupportedError, normalizeWorkdayState, pauseWorkday, resumeWorkday, setWorkdayMode, snoozeWorkday,
    startWorkday, stopWorkday, WORK_MODES,
} from './workdayApi';
import {createMutationEpoch, optimisticWorkday} from './unifiedTimer';
import haptics from './haptics';
import refreshWidgets from './widgets/widgetRefresh';
import {subscribeWorkdayReminder} from './workdayBus';
import {canAttemptWorkFix, getQuickFix} from './workLocation';
import {cancelWorkdayReminder} from './workdayReminder';
import {
    dayKeyOf, dayRolledOver, isPausedState, isReminderDue, isRouteAllowed, liveBreakSec, liveElapsedSec, pickSurface, progressModel, reminderPlan, resolveStartPrompt,
    shouldRefresh, startDismissKey, startPromptHost, targetSec, workedTodaySec,
} from './workdayRules';

const POLL_MS = 60 * 1000;
/** AsyncStorage key of the user's last manual office / home-office choice (sent as the no-GPS fallback). */
const WORK_MODE_PREF_KEY = (uid) => `HG_WORKDAY_MODE_PREF:${uid}`;

const noop = () => {};
const asyncNull = async () => null;

/** Context default: a harmless "feature unavailable" value so consumers outside the provider render nothing. */
const DEFAULT_VALUE = Object.freeze({
    supported: false,
    loaded: false,
    busy: false,
    state: null,
    running: false,
    timerRunning: false,
    surface: 'none',
    refresh: asyncNull,
    start: asyncNull,
    stop: asyncNull,
    pause: asyncNull,
    resume: asyncNull,
    paused: false,
    snooze: asyncNull,
    setWorkMode: asyncNull,
    workModePref: 'OFFICE',
    setWorkModePref: noop,
    gpsWorkMode: false,
    sheetOpen: false,
    openSheet: noop,
    closeSheet: noop,
    openReminder: noop,
    closeReminder: noop,
    dismissStart: noop,
    skipStartToday: noop,
    reportBriefingDecision: noop,
    setBriefingVisible: noop,
});

export const WorkdayContext = createContext(DEFAULT_VALUE);

/**
 * Hook for the workday state + actions.
 * @returns {object} context value (a safe "unsupported" default outside the provider).
 */
export const useWorkday = () => useContext(WorkdayContext);

/**
 * Ticking, derived numbers for a clock UI. Only the component calling this re-renders on each tick.
 * @param {number} [intervalMs] tick period; pass 0 to freeze (no timer when nothing runs).
 * @returns {{running: boolean, paused: boolean, breakSec: number, elapsedSec: number, workedSec: number, targetSec: number, progress: number,
 *   remainingSec: number, overtimeSec: number, tone: 'ok'|'warn'|'danger'}} live model.
 */
export const useWorkdayLive = (intervalMs = 1000) => {
    const {state} = useWorkday();
    const [now, setNow] = useState(() => Date.now());
    const running = !!state?.running;

    useEffect(() => {
        if (!running || !intervalMs) {
            return undefined;
        }

        setNow(Date.now());

        const id = setInterval(() => setNow(Date.now()), intervalMs);

        return () => clearInterval(id);
    }, [running, intervalMs]);

    return useMemo(() => {
        const clock = running ? now : Date.now();
        const worked = state ? workedTodaySec(state, clock) : 0;
        const target = state ? targetSec(state) : 0;

        return {
            running,
            paused: isPausedState(state),
            breakSec: state ? liveBreakSec(state, clock) : 0,
            elapsedSec: state ? liveElapsedSec(state, clock) : 0,
            workedSec: worked,
            targetSec: target,
            ...progressModel(worked, target || 480 * 60),
        };
    }, [state, now, running]);
};

const currentRouteName = () => {
    try {
        return navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name : '';
    } catch (_e) {
        return '';
    }
};

/**
 * @param {{isLoggedIn: boolean, children: React.ReactNode}} props `isLoggedIn` gates every request.
 * @returns {JSX.Element} provider.
 */
export function WorkdayProvider({isLoggedIn, children}) {
    const [userId, setUserId] = useState('');
    const [state, setState] = useState(null);
    const [supported, setSupported] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reminderOpen, setReminderOpen] = useState(false);
    const [reminderForced, setReminderForced] = useState(false);
    const [reminderDismissedKey, setReminderDismissedKey] = useState('');
    const [briefingVisible, setBriefingVisible] = useState(false);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [briefingDecision, setBriefingDecision] = useState(null);
    const [startDismissedToday, setStartDismissedToday] = useState(false);
    const [startSessionClosed, setStartSessionClosed] = useState(false);
    const [routeName, setRouteName] = useState('');
    const [dueTick, setDueTick] = useState(0);
    const [workModePref, setWorkModePrefState] = useState('OFFICE');
    const [gpsWorkMode, setGpsWorkMode] = useState(false);

    const stateRef = useRef(null);
    const userRef = useRef('');
    const supportedRef = useRef(true);
    const inflight = useRef(false);
    // Optimistic mutations bump the epoch; a read that started before the latest one is stale and discarded.
    const epochRef = useRef(null);
    if (epochRef.current === null) {
        epochRef.current = createMutationEpoch();
    }
    const lastFetchAt = useRef(0);
    const dayKeyRef = useRef(dayKeyOf());

    stateRef.current = state;
    userRef.current = userId;
    supportedRef.current = supported;

    const ready = !!isLoggedIn && !!userId;

    /* ------------------------------ user + route ------------------------------ */

    useEffect(() => {
        let cancelled = false;

        if (!isLoggedIn) {
            setUserId('');
            setState(null);
            setLoaded(false);
            setReminderOpen(false);
            setBriefingDecision(null);
            cancelWorkdayReminder(userRef.current);

            return undefined;
        }

        getUserInfo().then((u) => {
            if (!cancelled) {
                setUserId(String(u?._id || u?.id || ''));
            }
        }).catch(noop);

        return () => { cancelled = true; };
    }, [isLoggedIn]);

    useEffect(() => {
        if (!ready) {
            return undefined;
        }

        const read = () => setRouteName(currentRouteName() || '');

        read();

        const unsub = navigationRef.addListener ? navigationRef.addListener('state', read) : null;
        // The container may not be ready yet on a cold start: look again shortly.
        const retry = setTimeout(read, 1500);

        return () => {
            clearTimeout(retry);

            try {
                unsub?.();
            } catch (_e) {
                // already torn down
            }
        };
    }, [ready]);

    // "Start prompt dismissed today" is persisted per user + local day; re-evaluated on user change and on day rollover.
    const loadStartDismissed = useCallback((uid) => {
        if (!uid) {
            return;
        }

        AsyncStorage.getItem(startDismissKey(uid, dayKeyOf())).then((v) => setStartDismissedToday(v === '1')).catch(noop);
    }, []);

    useEffect(() => {
        loadStartDismissed(userId);
    }, [userId, loadStartDismissed]);

    // Remembered manual office / home-office choice of this user.
    useEffect(() => {
        if (!userId) {
            return;
        }

        AsyncStorage.getItem(WORK_MODE_PREF_KEY(userId))
            .then((v) => setWorkModePrefState(WORK_MODES.includes(v) ? v : 'OFFICE'))
            .catch(noop);
    }, [userId]);

    // Whether the clock-in GPS fix will decide office / home office (re-checked while idle; a store setting may change).
    const isRunning = !!state?.running;

    useEffect(() => {
        let cancelled = false;

        if (!ready || isRunning) {
            return undefined;
        }

        canAttemptWorkFix('clock_in').then((ok) => {
            if (!cancelled) {
                setGpsWorkMode(ok);
            }
        }).catch(noop);

        return () => { cancelled = true; };
    }, [ready, isRunning]);

    const setWorkModePref = useCallback((mode) => {
        if (!WORK_MODES.includes(mode)) {
            return;
        }

        setWorkModePrefState(mode);

        if (userRef.current) {
            AsyncStorage.setItem(WORK_MODE_PREF_KEY(userRef.current), mode).catch(noop);
        }
    }, []);

    /** Reset the day-scoped flags when the local day changed while the app stayed in memory. */
    const checkDayRollover = useCallback(() => {
        const nowKey = dayKeyOf();

        if (dayRolledOver(dayKeyRef.current, nowKey)) {
            dayKeyRef.current = nowKey;
            setStartDismissedToday(false);
            setStartSessionClosed(false);
            setReminderDismissedKey('');
            loadStartDismissed(userRef.current);
        }
    }, [loadStartDismissed]);

    /* --------------------------------- fetching -------------------------------- */

    const apply = useCallback((next) => {
        if (next) {
            setState(next);
            setLoaded(true);
        }
    }, []);

    const refresh = useCallback(async (force = false) => {
        // `force` (after a refused mutation) must not be swallowed by a poll that is already in flight: that read
        // may predate the mutation, and its answer is dropped by the epoch guard below.
        if (!userRef.current || (inflight.current && force !== true)) {
            return stateRef.current;
        }

        // Unsupported backend (404/405): probe at most every 10 min instead of every poll tick.
        if (force !== true && !shouldRefresh(supportedRef.current, lastFetchAt.current)) {
            return stateRef.current;
        }

        inflight.current = true;
        lastFetchAt.current = Date.now();

        const startedEpoch = epochRef.current.current();

        try {
            const res = await fetchWorkdayState({dayKey: dayKeyOf()});

            if (epochRef.current.isStale(startedEpoch)) {
                // A pause / resume / stop ran while this read was in flight: it describes the old world.
                return stateRef.current;
            }

            if (res.unsupported) {
                setSupported(false);
                setState(null);
                setLoaded(true);
            } else if (res.ok) {
                setSupported(true);
                apply(res.state);
            }

            return res.state || null;
        } catch (_e) {
            return stateRef.current;
        } finally {
            inflight.current = false;
        }
    }, [apply]);

    // Initial fetch on login + foreground return + 60 s poll while active.
    useEffect(() => {
        if (!ready) {
            return undefined;
        }

        refresh();

        const tick = () => {
            checkDayRollover();
            refresh();
        };
        let poll = AppState.currentState === 'active' ? setInterval(tick, POLL_MS) : null;
        const sub = AppState.addEventListener('change', (next) => {
            if (next === 'active') {
                checkDayRollover();
                refresh();
                setDueTick((n) => n + 1);

                if (!poll) {
                    poll = setInterval(tick, POLL_MS);
                }
            } else if (poll) {
                clearInterval(poll);
                poll = null;
            }
        });

        return () => {
            sub?.remove?.();

            if (poll) {
                clearInterval(poll);
            }
        };
    }, [ready, refresh, checkDayRollover]);

    // Realtime: `workday:changed` pushes a full WorkdayState to the user's room.
    useEffect(() => {
        if (!ready || !supported) {
            return undefined;
        }

        let cancelled = false;
        let socket = null;

        (async () => {
            try {
                const token = await getAccessToken();

                if (cancelled || !userRef.current) {
                    return;
                }

                socket = createSocketClient(ApiConfig.apiURL, {
                    transports: ['websocket', 'polling'],
                    timeout: 5000,
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 5000,
                    auth: token ? {token, userId: userRef.current} : {userId: userRef.current},
                });
                socket.on('connect', () => socket.emit('join:user', userRef.current));
                socket.on('workday:changed', (payload) => {
                    const next = normalizeWorkdayState(payload?.data || payload);

                    if (next) {
                        apply(next);
                    } else {
                        refresh();
                    }
                });
            } catch (_e) {
                // best effort — the poll + foreground refetch remain
            }
        })();

        return () => {
            cancelled = true;

            try {
                socket?.removeAllListeners();
                socket?.disconnect();
            } catch (_e) {
                // teardown race
            }
        };
    }, [ready, supported, apply, refresh]);

    /* ------------------------- reminder: server push only ------------------------- */

    const runningId = state?.running?.id || '';
    const reminderAt = state?.running?.reminderAt || '';

    // The server push is the single OS notification. One-time cleanup of a legacy locally scheduled reminder.
    useEffect(() => {
        if (userId && Platform.OS !== 'web') {
            cancelWorkdayReminder(userId);
        }
    }, [userId]);

    // In-app timer: open the reminder when `reminderAt` is reached while the app is in the foreground.
    useEffect(() => {
        if (!state?.running) {
            return undefined;
        }

        const plan = reminderPlan(state, Date.now());

        if (plan.action !== 'schedule') {
            return undefined;
        }

        const wait = Math.min(plan.at - Date.now(), 2147483000);
        const id = setTimeout(() => setDueTick((n) => n + 1), Math.max(wait, 0) + 250);

        return () => clearTimeout(id);
    }, [state]);

    const dueKey = `${runningId}:${reminderAt}`;
    // `dueTick` is only a re-evaluation trigger (timer fired / app foregrounded).
    const due = useMemo(() => isReminderDue(state, Date.now()), [state, dueTick]); // eslint-disable-line react-hooks/exhaustive-deps

    // A due reminder opens the modal once per (session, reminderAt); a snooze changes reminderAt -> eligible again.
    useEffect(() => {
        if (due && dueKey !== reminderDismissedKey) {
            setReminderOpen(true);
        } else if (!due && !reminderForced) {
            setReminderOpen(false);
        }
    }, [due, dueKey, reminderDismissedKey, reminderForced]);

    // Notification / server-push tap -> open the reminder (forced, even when previously dismissed).
    useEffect(() => subscribeWorkdayReminder(() => {
        setReminderForced(true);
        setReminderDismissedKey('');
        setReminderOpen(true);
        refresh();
    }), [refresh]);

    const closeReminder = useCallback(() => {
        setReminderOpen(false);
        setReminderForced(false);
        setReminderDismissedKey(dueKey);
    }, [dueKey]);

    const openSheet = useCallback(() => setSheetOpen(true), []);
    const closeSheet = useCallback(() => setSheetOpen(false), []);

    const openReminder = useCallback(() => {
        setReminderForced(true);
        setReminderOpen(true);
    }, []);

    /* --------------------------------- actions -------------------------------- */

    const toast = useCallback((key, params) => {
        try {
            crossToast.show(i18n.t(key, params), 2800);
        } catch (_e) {
            // toast host unavailable
        }
    }, []);

    const fail = useCallback(async (error) => {
        if (error?.unsupported || isUnsupportedError(error)) {
            setSupported(false);

            return;
        }

        if (error?.code === 'WORK_MODE_GPS_LOCKED') {
            toast('workday_toast_mode_gps_locked');
            await refresh();

            return;
        }

        if (Number(error?.status) === 409 || error?.code === 'WORKDAY_NOT_RUNNING') {
            toast('workday_toast_already_stopped');
            await refresh(true);

            return;
        }

        toast('workday_toast_error');
    }, [refresh, toast]);

    const start = useCallback(async (opts = {}) => {
        if (busy) {
            return null;
        }

        setBusy(true);

        try {
            // Server records the clock_in ping from `location`; null fix / denied permission never blocks the start.
            // The fix (when the store enabled it) decides office / home office server-side; the pref is the fallback.
            const location = await getQuickFix({source: 'clock_in'});
            const body = {workMode: workModePref, ...opts};
            const next = await startWorkday(location ? {...body, location} : body);

            apply(next);
            Promise.resolve(refreshWidgets()).catch(() => {});
            toast('workday_toast_started');

            return next;
        } catch (error) {
            await fail(error);

            return null;
        } finally {
            setBusy(false);
        }
    }, [apply, busy, fail, toast, workModePref]);

    /**
     * Switch the running day between office and home office (manual sessions only; GPS-decided ones are locked).
     * Also remembered as the fallback of the next start.
     * @param {'OFFICE'|'HOME_OFFICE'} mode work mode. @returns {Promise<object|null>} state, or null on failure.
     */
    const setWorkMode = useCallback(async (mode) => {
        if (busy || !WORK_MODES.includes(mode)) {
            return null;
        }

        setWorkModePref(mode);

        if (!stateRef.current?.running || stateRef.current.running.workMode === mode) {
            return stateRef.current;
        }

        setBusy(true);

        try {
            const next = await setWorkdayMode(mode);

            apply(next);

            return next;
        } catch (error) {
            await fail(error);

            return null;
        } finally {
            setBusy(false);
        }
    }, [apply, busy, fail, setWorkModePref]);

    /**
     * Run one optimistic workday mutation: apply the predicted state at once, call the server, adopt its answer, and
     * ROLL BACK to the previous state (then re-read the truth) when it fails.
     * @param {'pause'|'resume'|'stop'} verb verb.
     * @param {() => Promise<object>} call server call.
     * @returns {Promise<object|null>} server state, or null on failure / no-op.
     */
    const mutate = useCallback(async (verb, call) => {
        const previous = stateRef.current;
        const predicted = optimisticWorkday(previous, verb, Date.now());

        if (!previous || predicted === previous) {
            return null;
        }

        epochRef.current.begin();
        setState(predicted);
        setBusy(true);

        try {
            const next = await call();

            epochRef.current.begin();
            apply(next);
            // Repaint the home-screen widgets (Android + iOS timelines) from the new truth right away.
            Promise.resolve(refreshWidgets()).catch(() => {});

            return next;
        } catch (error) {
            epochRef.current.begin();
            setState(previous);
            haptics.error();
            await fail(error);

            return null;
        } finally {
            setBusy(false);
        }
    }, [apply, fail]);

    const stop = useCallback(async ({stopTimers = false, note = ''} = {}) => {
        if (busy) {
            return null;
        }

        // Clock-out must never wait on a permission prompt or a slow GPS: a short, non-prompting fix only.
        const location = await getQuickFix({source: 'clock_out', prompt: false, timeoutMs: 1500});
        const next = await mutate('stop', () => stopWorkday(location ? {stopTimers, note, location} : {stopTimers, note}));

        if (next) {
            setReminderOpen(false);
            setReminderForced(false);
            cancelWorkdayReminder(userRef.current);
            haptics.success();
            toast('workday_toast_stopped');
        }

        return next;
    }, [busy, mutate, toast]);

    /** Pause the workday (open a break). Worked time freezes immediately; rolled back when the server refuses. */
    const pause = useCallback(async () => {
        if (busy) {
            return null;
        }

        const next = await mutate('pause', () => pauseWorkday({}));

        if (next) {
            haptics.medium();
            toast('workday_toast_paused');
        }

        return next;
    }, [busy, mutate, toast]);

    /** Resume the workday (end the break). */
    const resume = useCallback(async () => {
        if (busy) {
            return null;
        }

        const next = await mutate('resume', () => resumeWorkday({}));

        if (next) {
            haptics.medium();
            toast('workday_toast_resumed');
        }

        return next;
    }, [busy, mutate, toast]);

    const snooze = useCallback(async (minutes) => {
        if (busy) {
            return null;
        }

        setBusy(true);

        try {
            const next = await snoozeWorkday(minutes);

            apply(next);
            setReminderOpen(false);
            setReminderForced(false);
            toast('workday_toast_snoozed', {minutes});

            return next;
        } catch (error) {
            await fail(error);

            return null;
        } finally {
            setBusy(false);
        }
    }, [apply, busy, fail, toast]);

    /* ------------------------- start prompt + sequencing ------------------------- */

    const persistStartDismiss = useCallback(() => {
        setStartDismissedToday(true);

        if (userRef.current) {
            AsyncStorage.setItem(startDismissKey(userRef.current, dayKeyOf()), '1').catch(noop);
        }
    }, []);

    /** "Később": close for this app session only. */
    const dismissStart = useCallback(() => setStartSessionClosed(true), []);

    /** "Ma nem kérem": do not ask again today. */
    const skipStartToday = useCallback(() => {
        setStartSessionClosed(true);
        persistStartDismiss();
    }, [persistStartDismiss]);

    /**
     * The briefing reports its auto-open decision. When it opens it hosts the start prompt, so the prompt counts as shown.
     * @param {{open?: boolean, reason?: string}|null} decision {@link module:helpers/myDayBriefing.resolveAutoOpen} result.
     */
    const reportBriefingDecision = useCallback((decision) => {
        setBriefingDecision(decision || null);

        if (decision?.open) {
            persistStartDismiss();
        }
    }, [persistStartDismiss]);

    const startedToday = !!state && (state.today.sessions.length > 0 || !!state.running);
    const routeOk = isRouteAllowed(routeName);
    const startDecision = resolveStartPrompt({
        ready: ready && routeOk,
        supported,
        loaded,
        running: !!state?.running,
        startedToday,
        dismissedToday: startDismissedToday || startSessionClosed,
    });
    const startWanted = startDecision.show && startPromptHost(briefingDecision) === 'standalone';
    const reminderWanted = reminderOpen && !!state?.running && supported;

    const surface = ready
        ? pickSurface({briefingVisible, sheetOpen, reminderWanted, startWanted, routeAllowed: routeOk})
        : 'none';

    const value = useMemo(() => ({
        supported,
        loaded,
        busy,
        state,
        running: !!state?.running,
        timerRunning: !!state?.timer?.running,
        surface,
        sheetOpen,
        openSheet,
        closeSheet,
        refresh,
        start,
        stop,
        pause,
        resume,
        paused: !!state?.running && state.paused === true,
        snooze,
        setWorkMode,
        workModePref,
        setWorkModePref,
        gpsWorkMode,
        openReminder,
        closeReminder,
        dismissStart,
        skipStartToday,
        reportBriefingDecision,
        setBriefingVisible,
    }), [supported, loaded, busy, state, surface, sheetOpen, openSheet, closeSheet, refresh, start, stop, snooze, setWorkMode, workModePref,
        pause, resume, setWorkModePref, gpsWorkMode, openReminder, closeReminder, dismissStart, skipStartToday, reportBriefingDecision]);

    return <WorkdayContext.Provider value={value}>{children}</WorkdayContext.Provider>;
}

export default WorkdayProvider;
